// src/pages/DeclarationsPage.js
// Admin review of:
//   1. declarations collection — daily confirmations + decline events
//   2. adminAlerts collection  — system alerts (declaration declines, etc.)
// Actions: view driver profile, reactivate suspended account, mark alert resolved
import React, { useState, useEffect } from 'react';
import {
  collection, query, getDocs, doc, getDoc,
  updateDoc, limit, serverTimestamp, where,
} from 'firebase/firestore';
import { db } from '../firebase';
import Paginator from '../components/Paginator';

const PAGE_SIZE = 15;

const fmtTime = (ts) => {
  if (!ts?.seconds) return '—';
  return new Date(ts.seconds * 1000).toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function DeclarationsPage() {
  const [tab, setTab]         = useState('alerts');   // 'alerts' | 'declines' | 'log'
  const [alerts, setAlerts]   = useState([]);
  const [declines, setDeclines] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [drivers, setDrivers] = useState({}); // uid → profile cache
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [page, setPage]             = useState(1);
  const [exportUid, setExportUid]   = useState('');
  const [exportName, setExportName] = useState('');
  const [exporting, setExporting]   = useState(false);

  // ── Load data ─────────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      // Wrap entire load in try/catch so Firestore errors
      // don't propagate and crash the admin app auth state
      // Admin alerts — separate try/catch so failure here doesn't block declarations
      let alertData = [];
      try {
        const alertSnap = await getDocs(
          query(collection(db, 'adminAlerts'), limit(100))
        );
        // Document-review submissions (CDL / abstract / proof of work) belong in
        // the Driver Verification queue, not here. "Declarations & Alerts" only
        // surfaces declaration + system alerts, so filter those types out
        // (handles any historical alerts already written to the collection).
        const DOC_REVIEW_TYPES = new Set([
          'license_pending_review', 'abstract_pending_review', 'pow_pending_review',
        ]);
        alertData = alertSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => !DOC_REVIEW_TYPES.has(a.type))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setAlerts(alertData);
      } catch (alertErr) {
        console.warn('adminAlerts fetch failed (check rules):', alertErr.message);
        setAlerts([]);
      }

      // Fetch ALL declarations — filter client-side, no where() needed
      let allDecls = [];
      try {
        const allDeclSnap = await getDocs(
          query(collection(db, 'declarations'), limit(500))
        );
        allDecls = allDeclSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Declines
        setDeclines(
          allDecls
            .filter(d => d.result === 'declined_suspended')
            .sort((a, b) => (b.declinedAt?.seconds || 0) - (a.declinedAt?.seconds || 0))
        );

        // Full log
        setLogEntries(
          [...allDecls].sort((a, b) => {
            const aTs = a.confirmedAt?.seconds || a.declinedAt?.seconds || 0;
            const bTs = b.confirmedAt?.seconds || b.declinedAt?.seconds || 0;
            return bTs - aTs;
          })
        );

      } catch (declErr) {
        console.warn('declarations fetch failed:', declErr.message);
        setDeclines([]);
        setLogEntries([]);
      }

      // Prefetch driver profiles for all unique UIDs
      // Prefetch driver profiles for all unique UIDs
      const uids = [...new Set([
        ...alertData.map(a => a.driverId).filter(Boolean),
        ...allDecls.map(d => d.uid).filter(Boolean),
      ])];
      const driverCache = {};
      await Promise.all(uids.map(async uid => {
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (snap.exists()) driverCache[uid] = snap.data();
        } catch (_) {}
      }));
      setDrivers(driverCache);
    } catch (err) {
      console.error('DeclarationsPage load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [tab]);

  // ── Reactivate suspended driver ───────────────────────────────────────────
  const handleReactivate = async (uid, alertId) => {
    const driver = drivers[uid];
    const name = driver?.fullName || uid;
    if (!window.confirm(
      `Reactivate ${name}?\n\nThis will:\n` +
      `• Set accountStatus → active\n` +
      `• Clear suspension reason\n` +
      `• Mark this alert as resolved\n\n` +
      `Only reactivate after verifying their licence is valid.`
    )) return;

    setProcessing(alertId || uid);
    try {
      await updateDoc(doc(db, 'users', uid), {
        accountStatus:    'active',
        suspensionReason: null,
        reactivatedAt:    serverTimestamp(),
        reactivatedBy:    'admin',
        updatedAt:        serverTimestamp(),
      });
      if (alertId) {
        await updateDoc(doc(db, 'adminAlerts', alertId), {
          resolved:   true,
          resolvedAt: serverTimestamp(),
        });
      }
      load();
    } catch (err) {
      alert('Error reactivating driver. Please try again.');
      console.error(err);
    } finally {
      setProcessing(null);
    }
  };

  // ── Mark alert resolved ───────────────────────────────────────────────────
  const handleResolve = async (alertId) => {
    setProcessing(alertId);
    try {
      await updateDoc(doc(db, 'adminAlerts', alertId), {
        resolved:   true,
        resolvedAt: serverTimestamp(),
      });
      load();
    } catch (err) {
      alert('Error resolving alert.');
    } finally {
      setProcessing(null);
    }
  };

  // ── Export court-ready declaration log for a driver ─────────────────────
  const handleExport = async () => {
    const uid = exportUid.trim();
    if (!uid) { alert('Enter a driver UID to export.'); return; }
    setExporting(true);
    try {
      let driverProfile = drivers[uid];
      if (!driverProfile) {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) { driverProfile = snap.data(); }
        else { alert('Driver not found. Use the exact Firebase UID.'); setExporting(false); return; }
      }
      const declSnap = await getDocs(query(collection(db, 'declarations'), where('uid', '==', uid)));
      const entries = declSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aTs = a.confirmedAt?.seconds || a.declinedAt?.seconds || 0;
          const bTs = b.confirmedAt?.seconds || b.declinedAt?.seconds || 0;
          return bTs - aTs;
        });
      if (entries.length === 0) { alert('No declaration records found for this driver.'); setExporting(false); return; }

      const name  = driverProfile?.fullName || 'Unknown';
      const email = driverProfile?.email    || 'N/A';
      const phone = driverProfile?.phone    || 'N/A';
      const now   = new Date().toLocaleString('en-CA', { timeZone: 'America/Toronto' });
      const declineCount = entries.filter(e => e.result === 'declined_suspended').length;

      const rows = entries.map((e, i) => {
        const ts = e.confirmedAt || e.declinedAt;
        const tsStr = ts?.seconds
          ? new Date(ts.seconds * 1000).toLocaleString('en-CA', {
              timeZone: 'America/Toronto',
              year: 'numeric', month: 'long', day: 'numeric',
              hour: '2-digit', minute: '2-digit', second: '2-digit',
            })
          : 'Unknown';
        const result = e.result === 'declined_suspended'
          ? 'DECLINED — ACCOUNT SUSPENDED'
          : 'CONFIRMED — LICENCE VALID';
        const color  = e.result === 'declined_suspended' ? '#dc2626' : '#16a34a';
        const jobRef = e.triggeredByJobId || 'Daily check-in (no active job)';
        return `<tr style="background:${i % 2 === 0 ? '#fff' : '#f9f9f9'}">`
          + `<td style="padding:8px 12px;border:1px solid #ddd;font-size:12px;color:#666">${i + 1}</td>`
          + `<td style="padding:8px 12px;border:1px solid #ddd;font-size:12px">${tsStr} (ET)</td>`
          + `<td style="padding:8px 12px;border:1px solid #ddd;font-size:12px;color:${color};font-weight:600">${result}</td>`
          + `<td style="padding:8px 12px;border:1px solid #ddd;font-size:12px;color:#666">${jobRef}</td>`
          + `<td style="padding:8px 12px;border:1px solid #ddd;font-size:11px;font-family:monospace;color:#999">${e.id}</td>`
          + '</tr>';
      }).join('');

      const html = `<!DOCTYPE html><html><head><meta charset='utf-8'>
        <title>Declaration Log — ${name}</title>
        <style>
          body { font-family:Arial,sans-serif; margin:40px; color:#222; }
          h1   { font-size:22px; margin-bottom:4px; }
          .sub { color:#666; font-size:13px; margin-bottom:24px; }
          .legal { background:#fff3cd; border:1px solid #ffc107; border-radius:6px;
            padding:12px 16px; font-size:12px; color:#664d03; margin-bottom:24px; line-height:1.6; }
          .info  { background:#f8f9fa; border:1px solid #dee2e6; border-radius:6px;
            padding:14px 18px; margin-bottom:24px; border-collapse:collapse; width:100%; }
          .info td { padding:4px 16px 4px 0; font-size:13px; vertical-align:top; }
          .info td:first-child { color:#666; width:160px; font-weight:600; }
          table.log { width:100%; border-collapse:collapse; margin-bottom:32px; }
          table.log th { background:#1a1a1a; color:#fff; padding:10px 12px;
            font-size:12px; text-align:left; border:1px solid #333; }
          .footer { color:#999; font-size:11px; border-top:1px solid #eee; padding-top:12px; }
          @media print { body { margin:20px; } }
        </style></head><body>
        <h1>🚛 Crewboard — Daily Licence Declaration Log</h1>
        <div class='sub'>Official system-generated record for legal and regulatory use · Do not alter</div>
        <div class='legal'>
          <strong>Legal Notice:</strong> This document is an authentic, unedited export from the
          Crewboard platform database (crewboard.ca). Each entry represents a timestamped digital
          declaration made by the driver within the Crewboard mobile application. Under
          <strong>Section 11a of the Crewboard Terms of Service</strong>, drivers who confirm
          their licence is valid accept full legal responsibility for the accuracy of that
          declaration. Making a false declaration is a breach of contract and shifts all liability
          to the driver. This log may be submitted as evidence in legal, insurance, or regulatory
          proceedings. For verification: <strong>legal@crewboard.ca</strong>
        </div>
        <table class='info'><tbody>
          <tr><td>Driver Name</td><td><strong>${name}</strong></td></tr>
          <tr><td>Email</td><td>${email}</td></tr>
          <tr><td>Phone</td><td>${phone}</td></tr>
          <tr><td>Driver UID</td><td style='font-family:monospace;font-size:11px'>${uid}</td></tr>
          <tr><td>Total Declarations</td><td><strong>${entries.length}</strong></td></tr>
          <tr><td>Declines</td><td style='color:#dc2626;font-weight:600'>${declineCount}</td></tr>
          <tr><td>Report Generated</td><td>${now} (Eastern Time)</td></tr>
          <tr><td>Generated By</td><td>Crewboard Admin System</td></tr>
        </tbody></table>
        <table class='log'><thead><tr>
          <th>#</th><th>Timestamp (Eastern Time)</th><th>Declaration Result</th>
          <th>Triggered By (Job ID)</th><th>Declaration Record ID</th>
        </tr></thead><tbody>${rows}</tbody></table>
        <div class='footer'>
          Generated: ${now} · Crewboard (crewboard.ca) · Firebase Firestore (North America)
          · Verification: legal@crewboard.ca
        </div></body></html>`;

      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 600);

    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  const currentData = tab === 'alerts' ? alerts
    : tab === 'declines' ? declines
    : logEntries;
  const totalPages  = Math.ceil(currentData.length / PAGE_SIZE);
  const paged       = currentData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const unresolvedAlerts  = alerts.filter(a => !a.resolved).length;
  const unresolvedDeclines = declines.length;

  return (
    <div className="animate-fade">
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 800, color: 'var(--text)', margin: 0 }}>
          Declarations & Alerts
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>
          Daily licence declarations, suspension events, and system alerts
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <SummaryCard icon="🚨" value={unresolvedAlerts}  label="Unresolved Alerts"  color="#EF4444" />
        <SummaryCard icon="⛔" value={unresolvedDeclines} label="Declaration Declines" color="#EAB308" />
        <SummaryCard icon="✅" value={logEntries.filter(l => l.result !== 'declined_suspended').length} label="Confirmations (last 200)" color="#22C55E" />
      </div>

      {/* ── Export Panel ── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 14, padding: '20px 24px', marginBottom: 24,
        display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
            📄 Export Court-Ready Declaration Log
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 10 }}>
            Full timestamped report for a driver — print as PDF for legal use.
            Click any UID in the log below to copy it.
          </div>
          <input value={exportUid}
            onChange={e => { setExportUid(e.target.value); const d = drivers[e.target.value.trim()]; setExportName(d?.fullName || ''); }}
            placeholder='Paste driver Firebase UID here'
            style={{ width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '9px 14px', color: 'var(--text)', fontSize: 13,
              outline: 'none', boxSizing: 'border-box' }}
          />
          {exportName && <div style={{ color: '#22C55E', fontSize: 12, marginTop: 4 }}>✅ {exportName}</div>}
        </div>
        <button onClick={handleExport} disabled={exporting || !exportUid.trim()}
          style={{ background: (exporting || !exportUid.trim()) ? 'var(--surface2)' : '#F97316',
            border: 'none', borderRadius: 8, padding: '10px 20px',
            color: (exporting || !exportUid.trim()) ? 'var(--text-muted)' : '#fff',
            fontWeight: 700, fontSize: 13, cursor: exporting ? 'wait' : 'pointer', flexShrink: 0 }}>
          {exporting ? '⏳ Generating…' : '📄 Export & Print'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { id: 'alerts',   label: `🚨 Admin Alerts${unresolvedAlerts > 0 ? ` (${unresolvedAlerts})` : ''}` },
          { id: 'declines', label: `⛔ Declines${unresolvedDeclines > 0 ? ` (${unresolvedDeclines})` : ''}` },
          { id: 'log',      label: '📋 Full Declaration Log' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: tab === t.id ? '#F97316' : 'var(--surface)',
            border: `1px solid ${tab === t.id ? '#F97316' : 'var(--border)'}`,
            color: tab === t.id ? '#fff' : 'var(--text-secondary)',
            borderRadius: 8, padding: '8px 16px', fontSize: 13,
            fontWeight: 600, cursor: 'pointer',
          }}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60 }}>Loading…</div>
      ) : currentData.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60,
          background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--surface2)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>
            {tab === 'alerts' ? '✅' : tab === 'declines' ? '🎉' : '📋'}
          </div>
          <div>{tab === 'alerts' ? 'No unresolved alerts' : tab === 'declines' ? 'No declaration declines' : 'No declaration log entries'}</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {paged.map(item => {
              const uid    = item.driverId || item.uid;
              const driver = uid ? drivers[uid] : null;
              const isProcessing = processing === (item.id);

              if (tab === 'alerts') return (
                <AlertRow
                  key={item.id} item={item} driver={driver}
                  onReactivate={() => handleReactivate(uid, item.id)}
                  onResolve={() => handleResolve(item.id)}
                  isProcessing={isProcessing}
                />
              );
              if (tab === 'declines') return (
                <DeclineRow
                  key={item.id} item={item} driver={driver}
                  onReactivate={() => handleReactivate(uid, null)}
                  isProcessing={isProcessing}
                />
              );
              return <LogRow key={item.id} item={item} driver={driver} />;
            })}
          </div>
          {currentData.length > PAGE_SIZE && (
            <Paginator page={page} totalPages={totalPages}
              totalItems={currentData.length} pageSize={PAGE_SIZE} onChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ icon, value, label, color }) {
  return (
    <div style={{
      background: 'var(--surface)', border: `1px solid ${color}30`,
      borderRadius: 14, padding: '20px 24px', flex: 1, minWidth: 160,
    }}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: 'Syne' }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function AlertRow({ item, driver, onReactivate, onResolve, isProcessing }) {
  const isDeclaration = item.type === 'driver_declined_declaration';
  return (
    <div style={{
      background: 'var(--surface)',
      border: `1px solid ${item.resolved ? 'var(--surface2)' : 'rgba(239,68,68,0.3)'}`,
      borderRadius: 12, padding: '16px 20px',
      opacity: item.resolved ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 24, flexShrink: 0 }}>
          {isDeclaration ? '🚨' : '⚠️'}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14 }}>
              {item.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
            {item.resolved
              ? <span style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>✅ Resolved</span>
              : <span style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>⚠️ Unresolved</span>
            }
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, marginBottom: 6 }}>
            {item.message}
          </div>
          {driver && (
            <div style={{ color: 'var(--text-muted)', fontSize: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>👤 {driver.fullName}</span>
              <span>📧 {driver.email}</span>
              <span style={{
                color: driver.accountStatus === 'suspended' ? '#EF4444' :
                       driver.accountStatus === 'active'    ? '#22C55E' : 'var(--text-muted)',
                fontWeight: 600,
              }}>
                {driver.accountStatus === 'suspended' ? '⛔ Suspended' :
                 driver.accountStatus === 'active'    ? '✅ Active'    : driver.accountStatus}
              </span>
            </div>
          )}
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>
            {fmtTime(item.createdAt)}
            {item.jobId && <span style={{ marginLeft: 12 }}>🗂 Job: {item.jobId.slice(0, 12)}…</span>}
          </div>
        </div>
        {!item.resolved && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'flex-start' }}>
            {driver?.accountStatus === 'suspended' && item.driverId && (
              <button
                onClick={onReactivate} disabled={isProcessing}
                style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 8, padding: '7px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                {isProcessing ? '…' : '🔓 Reactivate'}
              </button>
            )}
            <button
              onClick={onResolve} disabled={isProcessing}
              style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8, padding: '7px 13px', fontSize: 12, cursor: 'pointer' }}
            >
              {isProcessing ? '…' : '✓ Resolve'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DeclineRow({ item, driver, onReactivate, isProcessing }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid rgba(234,179,8,0.3)',
      borderRadius: 12, padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 24 }}>⛔</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
            Declaration Declined
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 4 }}>
            {driver
              ? <><span>👤 {driver.fullName}</span><span>📧 {driver.email}</span></>
              : <span>UID: {item.uid?.slice(0, 16)}…</span>
            }
            <span>📅 {fmtTime(item.declinedAt)}</span>
            {item.triggeredByJobId && <span>🗂 Job: {item.triggeredByJobId.slice(0,12)}…</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {driver?.accountStatus === 'suspended'
              ? <span style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>⛔ Suspended</span>
              : <span style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>✅ Active (reactivated)</span>
            }
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Decl ID: {item.id?.slice(0,14)}…</span>
          </div>
        </div>
        {driver?.accountStatus === 'suspended' && item.uid && (
          <button
            onClick={onReactivate} disabled={isProcessing}
            style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 8, padding: '7px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
          >
            {isProcessing ? '…' : '🔓 Reactivate'}
          </button>
        )}
      </div>
    </div>
  );
}

function LogRow({ item, driver }) {
  const isDecline = item.result === 'declined_suspended';
  return (
    <div style={{
      background: 'var(--surface)',
      border: `1px solid ${isDecline ? 'rgba(239,68,68,0.25)' : 'var(--surface2)'}`,
      borderRadius: 10, padding: '12px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 18 }}>{isDecline ? '⛔' : '✅'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: isDecline ? '#EF4444' : '#22C55E', fontWeight: 600, fontSize: 13 }}>
            {isDecline ? 'Declined — Account Suspended' : 'Confirmed — Licence Valid'}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontWeight: driver?.fullName ? 500 : 400 }}>
              {driver?.fullName || '👤 Loading…'}
            </span>
            <span>{fmtTime(item.confirmedAt || item.declinedAt)}</span>
            {item.triggeredByJobId && <span>Job: {item.triggeredByJobId.slice(0,10)}…</span>}
            <span title='Click to copy UID for export'
              onClick={() => navigator.clipboard?.writeText(item.uid)}
              style={{ fontFamily: 'monospace', fontSize: 11, color: '#aaa',
                cursor: 'pointer', borderBottom: '1px dashed #ccc' }}>
              {item.uid?.slice(0, 14)}… ⎘
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}